import React, { useState, useEffect} from "react";
import { API } from "./config/api";
import { MapContainer, TileLayer, Polyline, useMap, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import {
  Wind,
  Map as MapIcon,
  AlertTriangle,
  User,
  Navigation,
  CheckCircle,
  Zap,
  TrendingUp,
  Shield,
  Leaf,
  BarChart2,
  Target,
  Star,
  Sun,
  Moon,
  Timer,
  Sparkles,
  ShieldCheck,
  Car,
  Bike,
  Footprints
} from "lucide-react";

delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png"
});

const getAlertIcon = (color) =>
  new L.DivIcon({
    className: "",
    html: `
      <div style="
        width:16px;
        height:16px;
        background:${color};
        border-radius:50%;
        border:2px solid white;
        box-shadow:0 0 8px ${color};
      "></div>
    `,
    iconSize: [16, 16]
  });


const AtmosphericBackground = () => (
  <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden bg-slate-950">
    <div className="absolute inset-0 bg-gradient-to-b from-[#051c15] via-[#020617] to-black opacity-90" />

    <div className="particles absolute inset-0 opacity-30">
      {[...Array(20)].map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full bg-emerald-400 blur-[1px]"
          style={{
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            width: `${Math.random() * 4 + 1}px`,
            height: `${Math.random() * 4 + 1}px`,
            animation: `float ${10 + Math.random() * 20}s infinite linear`,
            opacity: Math.random() * 0.5,
          }}
        />
      ))}
    </div>

    <div className="absolute inset-0 opacity-20">
      <div className="absolute top-0 left-0 w-[200%] h-full bg-gradient-to-r from-transparent via-emerald-900/20 to-transparent animate-drift" />
    </div>

    <div className="absolute inset-0 bg-[linear-gradient(rgba(0,255,156,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,255,156,0.03)_1px,transparent_1px)] bg-[size:100px_100px] [mask-image:radial-gradient(ellipse_at_center,black_40%,transparent_100%)]" />
  </div>
);


const GlassCard = ({ children, className = "", hoverEffect = false }) => (
  <div
    className={`
    relative backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl p-6 
    ${hoverEffect ? "hover:bg-white/10 hover:border-[#00FF9C]/30 transition-all duration-300 hover:shadow-[0_0_20px_rgba(0,255,156,0.1)]" : ""}
    ${className}
  `}
  >
    {children}
  </div>
);

const AnimatedNumber = ({ value }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let start = 0;
    const duration = 2000;
    const stepTime = 20;
    const steps = duration / stepTime;
    const increment = value / steps;
    const timer = setInterval(() => {
      start += increment;
      if (start >= value) {
        setDisplay(value);
        clearInterval(timer);
      } else {
        setDisplay(Math.floor(start));
      }
    }, stepTime);
    return () => clearInterval(timer);
  }, [value]);
  return <span>{display}</span>;
};


const HomeScreen = () => {

  const [aqiData, setAqiData] = useState(null);
  const [loadingAQI, setLoadingAQI] = useState(true);

  const getCategoryColor = (category) => {
    switch (category) {
      case "Good":
        return "bg-green-500 text-black";
      case "Satisfactory":
        return "bg-lime-400 text-black";
      case "Moderate":
        return "bg-yellow-400 text-black";
      case "Poor":
        return "bg-orange-500 text-black";
      case "Very Poor":
        return "bg-red-500 text-white";
      case "Severe":
        return "bg-red-700 text-white";
      default:
        return "bg-gray-500 text-white";
    }
  };

  const getTrendMessage = () => {
    if (!aqiData) return "";
    if (aqiData.trend === "Rising")
      return "Air quality is deteriorating across Delhi. Limit prolonged outdoor exposure.";
    if (aqiData.trend === "Improving")
      return "Air quality is gradually improving. Conditions slightly better than yesterday.";
    return "Air quality remains stable but sensitive groups should stay cautious.";
  };

  const generateForecast = () => {
    if (!aqiData) return [];
    const base = aqiData.overallAQI;
    return Array.from({ length: 12 }, (_, i) => {
      const variation =
        aqiData.trend === "Rising"
          ? i * 3
          : aqiData.trend === "Improving"
          ? -i * 2
          : (Math.sin(i) * 5);
      return Math.max(base + variation, 10);
    });
  };


  useEffect(() => {
    const fetchAQI = async () => {
      try {
        const data = await API.get("/api/aqi");
        setAqiData(data);
      } catch (err) {
        console.error("AQI Fetch Error:", err.message);
      } finally {
        setLoadingAQI(false);
      }
    };

    fetchAQI();
  }, []);

  return (
    <div className="space-y-8 animate-fadeIn pb-24">
      <section className="relative min-h-[60vh] flex flex-col items-center justify-center text-center p-4">
        <div className="absolute inset-0 radial-gradient-green opacity-20 pointer-events-none" />

        <div className="relative z-10 space-y-2">
          <h2 className="text-emerald-400/80 text-sm tracking-[0.3em] uppercase mb-4 animate-slideDown">
            Live Intelligence
          </h2>
          <div className="relative inline-block group cursor-default">
            <div className="absolute -inset-8 bg-emerald-500/20 rounded-full blur-3xl group-hover:bg-emerald-500/30 transition-all duration-1000 animate-pulse-slow"></div>
            <h1 className="text-6xl md:text-9xl font-black text-white tracking-tighter mb-2 relative z-10">
              {loadingAQI ? "..." : <AnimatedNumber value={aqiData?.overallAQI || 0} />}
            </h1>
            {aqiData && (
              <div
                className={`absolute top-2 right-0 text-xs font-bold px-3 py-1 rounded shadow-lg ${getCategoryColor(
                  aqiData.category
                )}`}
              >
                {aqiData.category.toUpperCase()}
              </div>
            )}
          </div>
          <p className="text-slate-300 max-w-md mx-auto text-base md:text-lg font-light">
            {getTrendMessage()}
          </p>
        </div>

        <div className="flex flex-wrap gap-3 mt-8 justify-center">
          {aqiData &&
            Object.entries(aqiData.pollutants).map(([key, value], i) => (

            <div
              key={i}
              className="px-4 py-2 rounded-full border border-white/10 bg-black/40 backdrop-blur-md text-xs font-mono text-emerald-100/70 hover:border-[#00FF9C] hover:text-[#00FF9C] transition-colors cursor-crosshair"
            >
              {key.toUpperCase()} {value}
            </div>
          ))}
        </div>
      </section>

      {aqiData && (
        <div className="text-xs text-slate-500 mt-4 text-center space-y-1">
          <div>
            Confidence:{" "}
            <span className="text-[#00FF9C] font-semibold">
              {aqiData.confidence}
            </span>
          </div>
          <div>
            Last Updated:{" "}
            {new Date(aqiData.lastUpdated).toLocaleTimeString()}
          </div>
        </div>
      )}


      {aqiData && (
        <div className="px-4 mt-6 md:mt-0 max-w-4xl mx-auto">
          <GlassCard
            className={`border-l-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 ${
              aqiData.trend === "Rising"
                ? "border-l-red-500 shadow-[0_0_30px_rgba(239,68,68,0.2)]"
                : aqiData.trend === "Improving"
                ? "border-l-green-500 shadow-[0_0_30px_rgba(34,197,94,0.2)]"
                : "border-l-yellow-500 shadow-[0_0_30px_rgba(245,158,11,0.2)]"
            }`}
          >
            <div className="flex items-start gap-4">
              <div
                className={`p-3 rounded-full ${
                  aqiData.trend === "Rising"
                    ? "bg-red-500/20 text-red-400"
                    : aqiData.trend === "Improving"
                    ? "bg-green-500/20 text-green-400"
                    : "bg-yellow-500/20 text-yellow-400"
                }`}
              >
                <AlertTriangle size={22} />
              </div>

              <div>
                <h3 className="text-white font-bold text-lg">
                  {aqiData.trend === "Rising"
                    ? "Pollution Levels Rising"
                    : aqiData.trend === "Improving"
                    ? "Air Quality Improving"
                    : "Air Quality Stable"}
                </h3>

                <p className="text-slate-400 text-sm mt-1">
                  {aqiData.trend === "Rising"
                    ? "AQI increased compared to previous reading. Avoid peak traffic exposure."
                    : aqiData.trend === "Improving"
                    ? "Pollution levels slightly reduced compared to previous measurement."
                    : "AQI levels remain consistent. Sensitive groups should remain cautious."}
                </p>
              </div>
            </div>
          </GlassCard>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-4 max-w-4xl mx-auto">
        <GlassCard>
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-slate-300 font-medium flex items-center gap-2">
              <Wind size={16} /> 12hr Forecast
            </h3>
            <span className="text-xs text-emerald-400">Stable Trend</span>
          </div>
          <div className="h-32 w-full flex items-end justify-between gap-1">
            {generateForecast().map((value, i) => (
              <div
                key={i}
                className="group relative w-full bg-emerald-900/30 rounded-t-sm hover:bg-[#00FF9C] transition-all duration-300"
                style={{ height: `${Math.min(value / 4, 100)}%` }}
              >
                <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-black text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap border border-white/10 z-10">
                  {Math.round(value)} AQI
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-[10px] text-slate-500 font-mono">
            <span>NOW</span>
            <span>+6H</span>
            <span>+12H</span>
          </div>
        </GlassCard>

        <GlassCard className="flex flex-col justify-center">
          <div className="flex items-center gap-2 mb-4 text-[#00FF9C]">
            <Zap size={18} />
            <span className="font-bold tracking-widest text-xs uppercase">
              System Insight
            </span>
          </div>
          <p className="text-lg text-slate-200 font-light leading-relaxed">
            "Wind speeds are dropping below{" "}
            <span className="text-[#00FF9C]">2.1 m/s</span>. Pollution
            dispersion will slow down tonight. Recommend minimizing outdoor
            cardio."
          </p>
        </GlassCard>
      </div>
    </div>
  );
};


const FitBounds = ({ routes, selectedRouteId }) => {
  const map = useMap();

  useEffect(() => {
    if (!routes || routes.length === 0) return;

    const selected = routes.find(r => r.routeId === selectedRouteId);

    if (!selected?.geometry?.coordinates) return;

    const latLngs = selected.geometry.coordinates.map(coord => [
      coord[1],
      coord[0]
    ]);

    map.flyToBounds(latLngs, {
      padding: [50, 50],
      animate: true,
      duration: 1.5,
      easeLinearity: 0.25
    });

  }, [routes, selectedRouteId, map]);

  return null;
};



const MapScreen = () => {
  const [routes, setRoutes] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [showAlerts, setShowAlerts] = useState(true);
  const [loadingAlerts, setLoadingAlerts] = useState(true);
  const [alertsError, setAlertsError] = useState(null);
  const [loadingRoutes, setLoadingRoutes] = useState(false);
  const [routeError, setRouteError] = useState(null);
  const [source, setSource] = useState("");
  const [destination, setDestination] = useState("");
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [routePriority, setRoutePriority] = useState("cleanest");
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [mapTheme, setMapTheme] = useState("dark");

  const isReady = source.trim() && destination.trim();

  const groupedRoutes = {
    car: routes.filter(r => r.mode === "car"),
    bike: routes.filter(r => r.mode === "bike"),
    walk: routes.filter(r => r.mode === "walk")
  };

  const fastestRoute = routes.reduce((prev, curr) =>
    curr.durationMin < prev.durationMin ? curr : prev,
    routes[0] || {}
  );

  const getRecommendedRoute = () => {
    if (!routes.length) return null;

    if (routePriority === "fastest") {
      return routes.reduce((prev, curr) =>
        curr.durationMin < prev.durationMin ? curr : prev
      );
    }

    if (routePriority === "safest") {
      return routes.reduce((prev, curr) =>
        curr.avgAQI < prev.avgAQI ? curr : prev
      );
    }

    return routes.reduce((prev, curr) =>
      curr.ecoScore > prev.ecoScore ? curr : prev
    );
  };

  const dynamicRecommended = getRecommendedRoute();

  useEffect(() => {
    if (dynamicRecommended?.routeId) {
      setSelectedRouteId(dynamicRecommended.routeId);
    }
  }, [dynamicRecommended, routePriority, routes]);

  const selectedRoute = routes.find(r => r.routeId === selectedRouteId);

  const timeDifference =
    selectedRoute && fastestRoute
      ? selectedRoute.durationMin - fastestRoute.durationMin
      : 0;

  const co2SavedVsCar =
    selectedRoute && selectedRoute.mode !== "car"
      ? (groupedRoutes.car[0]?.emission || 0) - selectedRoute.emission
      : 0;

  useEffect(() => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;

        try {
          const res = await API.get(
            "https://nominatim.openstreetmap.org/reverse",
            {
              params: {
                lat: latitude,
                lon: longitude,
                format: "json"
              }
            }
          );

          setSource(res.data.display_name);
        } catch (err) {
          console.error("Reverse geocode failed");
        }
      },
      () => {
        console.log("Location permission denied");
      }
    );
  }, []);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const response = await API.get("/api/alerts");

        if (!response.success || !Array.isArray(response.data)) {
          throw new Error("Invalid alert response");
        }

        setAlerts(response.data);
      } catch (err) {
        setAlertsError("Unable to load AQI alerts");
      } finally {
        setLoadingAlerts(false);
      }
    };
    fetchAlerts();
  }, []);

  useEffect(() => {
    if (!selectedRouteId) return;

    setIsTransitioning(true);

    const timer = setTimeout(() => {
      setIsTransitioning(false);
    }, 350);

    return () => clearTimeout(timer);

  }, [selectedRouteId]);

  return (
    <div className="relative h-screen w-full bg-[#051014] overflow-hidden flex flex-col">  

      {selectedRoute && (
        <div className="ml-auto mr-8 mt-8 w-96 z-40">
          <div className="backdrop-blur-2xl space-y-2 bg-gradient-to-br from-black/80 to-green-900/30 border border-[#00FF9C]/30 rounded-2xl p-4 shadow-[0_0_30px_rgba(0,255,156,0.2)]">

            <div className="text-xs text-[#00FF9C] uppercase tracking-wider mb-2">
              {routePriority === "fastest" && "Fastest Route Selected"}
              {routePriority === "cleanest" && "Cleanest Route Selected"}
              {routePriority === "safest" && "Safest Route Selected"}
            </div>

            <div className="text-lg font-bold text-white mb-3">
              {selectedRoute.mode === "car" && "🚗 Car Route"}
              {selectedRoute.mode === "bike" && "🚴 Bike Route"}
              {selectedRoute.mode === "walk" && "🚶 Walking Route"}
            </div>

            <div className="space-y-2 text-sm">

              <div className="flex justify-between">
                <span className="text-slate-400">Eco Score</span>
                <span className="text-[#00FF9C] font-semibold">
                  {selectedRoute.ecoScore}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-slate-400">AQI Exposure</span>
                <span>{selectedRoute.avgAQI}</span>
              </div>

              <div className="flex justify-between">
                <span className="text-slate-400">CO₂ Emission</span>
                <span>{selectedRoute.emission} kg</span>
              </div>

              {selectedRoute.mode !== "car" && (
                <div className="flex justify-between">
                  <span className="text-slate-400">CO₂ Saved vs Car</span>
                  <span className="text-[#00FF9C] font-semibold">
                    +{co2SavedVsCar.toFixed(2)} kg
                  </span>
                </div>
              )}

              <div className="flex justify-between">
                <span className="text-slate-400">Time Difference</span>
                <span className={
                  timeDifference > 0
                    ? "text-yellow-400"
                    : "text-[#00FF9C]"
                }>
                  {timeDifference > 0
                    ? `+${timeDifference} mins`
                    : `${Math.abs(timeDifference)} mins faster`}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 mb-10 md:mb-16 ml-4 mr-4 md:ml-auto md:mr-8 md:w-96 z-40">
        <GlassCard
          className="!p-0 max-h-[44vh] overflow-y-auto 
          bg-gradient-to-br 
          from-[#061f17]/90 
          via-[#05241c]/80 
          to-[#03120e]/90 
          border border-[#00FF9C]/20
          shadow-[0_20px_50px_rgba(0,255,156,0.08)]"
        >
          <div className="p-4 border-b border-white/10 bg-black/20">
            <h3 className="text-white font-bold flex items-center gap-2">
              <Navigation size={16} className="text-[#00FF9C]" /> Route
              Optimizer
            </h3>
          </div>
          <div className="p-4 space-y-4">
            <div className="space-y-2">
              <input
                type="text"
                placeholder="Source..."
                value={source}
                onChange={(e) => setSource(e.target.value)}
                className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00FF9C]"
              />

              <input
                type="text"
                placeholder="Destination..."
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-[#00FF9C]"
              />

              <div className="flex justify-center gap-3">
                <button
                  onClick={() => {
                    const temp = source;
                    setSource(destination);
                    setDestination(temp);
                  }}
                  className="bg-white/10 text-white px-4 py-2 text-sm rounded hover:bg-white/20 transition">
                  ⇅
                </button>

                <button
                  disabled={!isReady || loadingRoutes}
                  onClick={async () => {
                    if (!source || !destination) return;

                    setLoadingRoutes(true);
                    setRouteError(null);

                    try {
                      const response = await API.post("/api/routes", {
                        source,
                        destination
                      });

                      if (!response.success || !response.data?.routes) {
                        throw new Error("Invalid route response");
                      }

                      setRoutes(response.data.routes || []);
                      setSelectedRouteId(response.data.recommendedRouteId || null);
                    } catch (err) {
                      setRouteError("Failed to fetch routes");
                    } finally {
                      setLoadingRoutes(false);
                    }
                  }}
                  className={`px-4 py-2 text-sm rounded transition-all duration-300 flex items-center justify-center
                  ${isReady 
                    ? "bg-[#00FF9C] text-black shadow-[0_0_18px_rgba(0,255,156,0.5)]"
                    : "bg-[#00FF9C]/30 text-black cursor-not-allowed"}
                  `}>                  
                  {loadingRoutes ? (
                    <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <span className="font-semibold tracking-wide">
                      Find Route
                    </span>
                  )}
                </button>
              </div>
            </div>

            {!loadingRoutes && routes.length === 0 && (
              <div className="text-sm text-slate-500 text-center py-4">
                Enter Source & Destination to calculate cleanest route.
              </div>
            )}

            {loadingRoutes && (
              <div className="text-sm text-slate-400">
                Calculating cleanest routes...
              </div>
            )}

            {loadingAlerts && (
              <div className="absolute top-24 left-12 text-xs text-slate-400 z-50">
                Loading AQI alerts...
              </div>
            )}

            {alertsError && (
              <div className="absolute top-24 left-12 text-xs text-red-400 z-50">
                {alertsError}
              </div>
            )}

            {routeError && (
              <div className="text-sm text-red-400">
                {routeError}
              </div>
            )}

            {["car", "bike", "walk"].map((mode) => (
              groupedRoutes[mode]?.length > 0 && (
                <div key={mode} className="space-y-2">

                  <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400 mt-4">
                    {mode === "car" && (
                      <>
                        <Car size={14} className="text-blue-400" />
                        <span>Car Routes</span>
                      </>
                    )}

                    {mode === "bike" && (
                      <>
                        <Bike size={14} className="text-amber-400" />
                        <span>Bike Routes</span>
                      </>
                    )}

                    {mode === "walk" && (
                      <>
                        <Footprints size={14} className="text-emerald-400" />
                        <span>Walk Routes</span>
                      </>
                    )}
                  </div>

                  {groupedRoutes[mode].map((route) => {

                    const isBestOverall =
                      dynamicRecommended?.routeId === route.routeId;
                    const isSelected =
                      selectedRouteId === route.routeId;

                    return (
                      <div
                        key={route.routeId}
                        onClick={() => setSelectedRouteId(route.routeId)}
                        className={`p-3 rounded border relative cursor-pointer transition-all duration-300 ${
                          isSelected
                            ? "border-[#00FF9C] bg-[#00FF9C]/10 shadow-[0_0_25px_rgba(0,255,156,0.5)] scale-[1.02] animate-[softPulse_3s_infinite]"
                            : route.riskLevel === "High"
                            ? "border-red-500 bg-red-500/10"
                            : route.riskLevel === "Moderate"
                            ? "border-yellow-500 bg-yellow-500/10"
                            : "border-white/10 bg-white/5"
                        }`}
                      >

                        {isBestOverall && (
                          <div className="absolute top-0 left-0 bg-[#00FF9C] text-black text-xs font-bold px-3 py-1 rounded-br flex items-center gap-1 shadow-[0_0_12px_rgba(0,255,156,0.6)]">
                            <Star size={12} className="fill-black" />
                            BEST OVERALL
                          </div>
                        )}

                        <div className="flex justify-between items-center">

                          <div>
                            <div className="text-lg font-bold text-white">
                              {route.durationMin} mins
                            </div>
                            <div className="text-xs text-slate-400">
                              {route.distanceKm} km
                            </div>
                          </div>

                          <div className="text-right space-y-1">

                            <div className="text-xs text-slate-400">
                              AQI: {route.avgAQI}
                            </div>

                            <div className="text-xs text-slate-400">
                              CO₂: {route.emission} kg
                            </div>

                            <div className="text-xs text-[#00FF9C] font-semibold">
                              Eco Score: {route.ecoScore}
                            </div>

                            <div className={`text-xs font-bold ${
                              route.riskLevel === "High"
                                ? "text-red-400"
                                : route.riskLevel === "Moderate"
                                ? "text-yellow-400"
                                : "text-[#00FF9C]"
                            }`}>
                              {route.riskLevel}
                            </div>

                          </div>

                        </div>

                      </div>
                    );
                  })}
                </div>
              )
            ))}

          </div>
        </GlassCard>
      </div>

      <div className="w-full h-full relative opacity-40 md:opacity-50 transform scale-110">
        <svg className="w-full h-full" viewBox="0 0 800 600">
          <defs>
            <linearGradient
              id="roadGradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop offset="0%" stopColor="#1e293b" stopOpacity="0" />
              <stop offset="50%" stopColor="#334155" stopOpacity="1" />
              <stop offset="100%" stopColor="#1e293b" stopOpacity="0" />
            </linearGradient>
            <radialGradient id="heatGreen" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#00FF9C" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#00FF9C" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="heatRed" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#EF4444" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#EF4444" stopOpacity="0" />
            </radialGradient>
          </defs>

          <path
            d="M-100,500 Q400,300 900,400"
            stroke="url(#roadGradient)"
            strokeWidth="2"
            fill="none"
            className="animate-pulse"
          />
          <path
            d="M200,-100 Q300,300 250,700"
            stroke="url(#roadGradient)"
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M600,-100 Q500,300 650,700"
            stroke="url(#roadGradient)"
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M-100,200 Q400,250 900,100"
            stroke="url(#roadGradient)"
            strokeWidth="1"
            fill="none"
          />
        </svg>

        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-[#00FF9C] rounded-full shadow-[0_0_20px_#00FF9C] animate-ping" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-white rounded-full" />
      </div>

      <div className="absolute top-4 left-12 w-[65%] z-20 flex justify-between items-center">
        
        <div className="backdrop-blur-xl bg-black/70 border border-white/10 rounded-xl px-4 py-2 shadow-lg flex items-center gap-3">
          <span className="text-xs text-slate-300 tracking-wide">
            AQI Layer
          </span>

          <button
            onClick={() => setShowAlerts(prev => !prev)}
            className={`px-3 py-1 text-xs rounded-md transition ${
              showAlerts
                ? "bg-[#00FF9C]/20 text-[#00FF9C] border border-[#00FF9C]/40"
                : "bg-white/10 text-slate-400 border border-white/10"
            }`}
          >
            {showAlerts ? "Visible" : "Hidden"}
          </button>
        </div>

      </div>


      <div className="absolute top-12 left-12 w-[65%] h-[75%] z-10 rounded-3xl overflow-hidden border border-green-400/20
      backdrop-blur-2xl bg-gradient-to-br from-black/60 to-green-900/30 shadow-[0_0_40px_rgba(34,197,94,0.3)]">
        
        <div className="absolute top-4 right-4 z-[1000] backdrop-blur-xl bg-black/70 border border-white/10 rounded-xl px-4 py-2 shadow-lg">
          <div className="text-xs text-slate-400 mb-2 uppercase tracking-wider">
            Priority
          </div>

          <div className="flex gap-2">
            {["fastest", "cleanest", "safest"].map((type) => (
              <button
                key={type}
                onClick={() => setRoutePriority(type)}
                className={`px-3 py-1 text-xs rounded-md transition ${
                  routePriority === type
                    ? "bg-[#00FF9C]/20 text-[#00FF9C] border border-[#00FF9C]/40"
                    : "bg-white/10 text-slate-400 border border-white/10"
                }`}
              >
                {type === "fastest" && <Timer size={14} />}
                {type === "cleanest" && <Sparkles size={14} />}
                {type === "safest" && <ShieldCheck size={14} />}
              </button>
            ))}
          </div>

        </div>
        
        <div className="absolute bottom-4 right-4 z-[1000]">
          <button
            onClick={() =>
              setMapTheme(prev => (prev === "dark" ? "light" : "dark"))
            }
            className="w-11 h-11 flex items-center justify-center rounded-full 
                      backdrop-blur-xl bg-black/70 border border-[#00FF9C]/30 
                      shadow-[0_0_20px_rgba(0,255,156,0.3)] 
                      hover:scale-105 active:scale-95 transition-all duration-200"
          >
            {mapTheme === "dark" ? (
              <Moon size={18} className="text-[#00FF9C]" />
            ) : (
              <Sun size={18} className="text-[#00FF9C]" />
            )}
          </button>
        </div>

        <MapContainer
          center={[28.6139, 77.2090]}
          zoom={12}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom={false}
        >
          <TileLayer
            url={
              mapTheme === "dark"
                ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            }
            attribution='&copy; OpenStreetMap contributors &copy; CARTO'
          />

          <FitBounds routes={routes} selectedRouteId={selectedRouteId} />

          {showAlerts && alerts.map((alert) => (
            alert.lat && alert.lon ? (
              <Marker
                key={alert.uid}
                position={[alert.lat, alert.lon]}
                icon={getAlertIcon(alert.color)}
              >
                <Popup>
                  <div className="min-w-[180px] space-y-2 text-xs">
                    
                    <div className="text-sm font-bold text-white">
                      {alert.name}
                    </div>

                    <div className="flex justify-between">
                      <span className="text-slate-400">AQI</span>
                      <span className="font-bold">{alert.aqi}</span>
                    </div>

                    <div className="flex justify-between">
                      <span className="text-slate-400">Category</span>
                      <span
                        className={`font-semibold ${
                          alert.level === "Severe"
                            ? "text-red-500"
                            : alert.level === "Very Poor"
                            ? "text-red-400"
                            : alert.level === "Poor"
                            ? "text-orange-400"
                            : alert.level === "Moderate"
                            ? "text-yellow-400"
                            : "text-[#00FF9C]"
                        }`}
                      >
                        {alert.level}
                      </span>
                    </div>

                    <div className="text-slate-400">
                      {alert.advisory}
                    </div>

                    {alert.lastUpdated && (
                      <div className="text-[10px] text-slate-500">
                        Updated: {new Date(alert.lastUpdated).toLocaleTimeString()}
                      </div>
                    )}

                  </div>
                </Popup>
              </Marker>
            ) : null
          ))}


          {routes
            .filter(route => route.routeId === selectedRouteId)
            .map((route) => (

              <React.Fragment key={route.routeId}>

              <Polyline
                key={route.routeId}
                positions={route.geometry.coordinates.map(coord => [coord[1], coord[0]])}
                pathOptions={{
                  color:
                    route.mode === "car"
                      ? mapTheme === "dark" ? "#60a5fa" : "#2563eb"
                      : route.mode === "bike"
                      ? mapTheme === "dark" ? "#fbbf24" : "#d97706"
                      : mapTheme === "dark" ? "#34d399" : "#059669",
                  weight: 7,
                  opacity: isTransitioning ? 0.2 : 1,
                  dashArray:
                    route.mode === "walk"
                      ? "4"
                      : route.mode === "bike"
                      ? "8"
                      : null
                }}
              />

              {!isTransitioning && (
                <Polyline
                  positions={route.geometry.coordinates.map(coord => [coord[1], coord[0]])}
                  pathOptions={{
                    color: "#00FF9C",
                    weight: 14,
                    opacity: 0.05
                  }}
                />
                )}
                </React.Fragment>
          ))}
        </MapContainer>
      </div>
    </div>
  );
};




const ActionScreen = () => {
  const [sliderValue, setSliderValue] = useState(30);
  const [checked, setChecked] = useState([false, false, true]);
  const [simulationResult, setSimulationResult] = useState(null);
  const [loadingSim, setLoadingSim] = useState(false);
  const [simError, setSimError] = useState(null);
  const [profile, setProfile] = useState(null);

  const safeBaselinePM25 = simulationResult?.baseline?.pm25 || 0;
  const safeReductionPM25 = simulationResult?.reduction?.pm25 || 0;

  const handleCheck = (index) => {
    const newChecked = [...checked];
    newChecked[index] = !newChecked[index];
    setChecked(newChecked);
  };

  return (
    <div className="pt-24 px-4 pb-24 max-w-4xl mx-auto space-y-8 animate-fadeIn">
      <header>
        <h2 className="text-3xl font-bold text-white mb-1">Impact Engine</h2>
        <p className="text-slate-400">
          Simulate civic change & track your contribution.
        </p>
      </header>

      <GlassCard className="overflow-hidden border-[#00FF9C]/20 shadow-[0_0_30px_rgba(0,255,156,0.05)]">
        <div className="flex flex-col md:flex-row gap-8">
          <div className="flex-1 space-y-6">
            <div className="space-y-2">
              <label className="text-sm text-[#00FF9C] font-mono uppercase tracking-widest">
                Participation Simulator
              </label>
              <p className="text-white text-lg font-medium">
                If{" "}
                <span className="text-[#00FF9C] text-2xl">{sliderValue}%</span>{" "}
                of Delhi uses public transport...
              </p>
            </div>

            <div className="relative pt-6 pb-2">
              <input
                type="range"
                min="0"
                max="100"
                value={sliderValue}
                onChange={async (e) => {
                  const value = Number(e.target.value);
                  setSliderValue(value);

                  setLoadingSim(true);
                  setSimError(null);

                  try {
                    const result = await API.post("/api/simulate", {
                      transportShift: value
                    });

                    setSimulationResult(result.data);
                    const updatedProfile = await API.get("/api/profile/me");
                    setProfile(updatedProfile);

                  } catch (err) {
                    setSimError("Simulation failed");
                  } finally {
                    setLoadingSim(false);
                  }
                }}

                className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer accent-[#00FF9C]"
              />
              <div className="flex justify-between text-xs text-slate-500 mt-2 font-mono">
                <span>CURRENT: 12%</span>
                <span>TARGET: 100%</span>
              </div>
            </div>

            {loadingSim && (
              <div className="text-xs text-slate-400">
                Running emission model...
              </div>
            )}

            {simError && (
              <div className="text-xs text-red-400">
                {simError}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-black/40 p-3 rounded border border-white/5">
                <div className="text-slate-400 text-xs">PM2.5 Reduction</div>
                <div className="text-xl text-white font-bold">
                    {safeBaselinePM25 > 0
                      ? `-${Math.round((safeReductionPM25 / safeBaselinePM25) * 100)}%`
                      : "..."}
                </div>
              </div>
              <div className="bg-black/40 p-3 rounded border border-white/5">
                <div className="text-slate-400 text-xs">CO2 Avoided</div>
                <div className="text-xl text-white font-bold">
                  {simulationResult?.reduction?.co2
                    ? simulationResult.reduction.co2
                    : "..."}
                  <span className="text-xs text-slate-500"> tons/day</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 min-h-[200px] relative bg-gradient-to-t from-[#0B3D2E] to-transparent rounded-lg flex items-end justify-center overflow-hidden border border-white/5">
            <div
              className={`absolute inset-0 bg-black/60 transition-opacity duration-500 z-10`}
              style={{ opacity: 1 - sliderValue / 100 }}
            />
            <div className="relative z-0 text-center pb-4 w-full">
              <div className="flex items-end justify-center gap-1 h-32 px-4">
                {[30, 45, 60, 40, 70, 55, 65, 35, 50, 75].map((val, i) => (
                  <div
                    key={i}
                    className="w-full bg-[#00FF9C] transition-all duration-300"
                    style={{
                      height: `${val}%`,
                      opacity: 0.2 + sliderValue / 200,
                    }}
                  />
                ))}
              </div>
              <div className="text-xs text-[#00FF9C] font-mono mt-2 tracking-widest">
                AIR CLARITY PROJECTION
              </div>
            </div>
          </div>
        </div>
      </GlassCard>

      <div className="grid md:grid-cols-2 gap-6">
        <GlassCard>
          <h3 className="text-white font-bold mb-4 flex items-center gap-2">
            <CheckCircle size={18} className="text-[#00FF9C]" /> Daily Missions
          </h3>
          <div className="space-y-3">
            {[
              "Used Metro/Bus today",
              "Turned off idling engine",
              "Watered neighborhood plants",
            ].map((task, i) => (
              <div
                key={i}
                onClick={() => handleCheck(i)}
                className={`
                            flex items-center justify-between p-3 rounded cursor-pointer border transition-all duration-200
                            ${checked[i] ? "bg-[#00FF9C]/10 border-[#00FF9C] text-white" : "bg-black/20 border-white/5 text-slate-400 hover:bg-white/5"}
                            `}
              >
                <span className="text-sm">{task}</span>
                <div
                  className={`w-5 h-5 rounded-full border flex items-center justify-center ${checked[i] ? "bg-[#00FF9C] border-[#00FF9C]" : "border-slate-500"}`}
                >
                  {checked[i] && (
                    <CheckCircle size={12} className="text-black" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </GlassCard>

        <GlassCard className="flex flex-col items-center justify-center text-center">
          <div className="w-24 h-24 rounded-full border-4 border-slate-800 flex items-center justify-center relative mb-4">
            <svg className="absolute inset-0 w-full h-full -rotate-90">
              <circle
                cx="44"
                cy="44"
                r="40"
                stroke="#00FF9C"
                strokeWidth="4"
                fill="none"
                className="w-full h-full"
                strokeDasharray="251"
                strokeDashoffset="40"
              />
            </svg>
            <div className="text-3xl font-black text-white">
              {profile?.ecoPoints ?? 0}
            </div>
          </div>
          <div className="text-[#00FF9C] font-bold text-sm tracking-wider">
            GREEN SCORE
          </div>
          <p className="text-slate-500 text-xs mt-2">Top 5% in South Delhi</p>
        </GlassCard>
      </div>
    </div>
  );
};




const ReportScreen = () => {

  const [classification, setClassification] = useState("");
  const [image, setImage] = useState(null);
  const [location, setLocation] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [reportMessage, setReportMessage] = useState(null);
  const [reportError, setReportError] = useState(null);

  useEffect(() => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        });
      },
      () => {
        console.log("Location permission denied");
      }
    );
  }, []);

  return (
  <div className="pt-24 px-4 max-w-2xl mx-auto animate-fadeIn pb-24">
    <div className="text-center mb-8">
      <h2 className="text-2xl font-bold text-white">Citizen Report</h2>
      <p className="text-slate-400 text-sm">
        Flag pollution sources. Improve your ward.
      </p>
    </div>

    <GlassCard className="space-y-6">
      <div className="space-y-2">
        <label className="text-xs text-[#00FF9C] font-bold uppercase">
          Incident Type
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {['Waste Burning', 'Construction', 'Vehicle Smoke'].map(type => (
            <button
              key={type}
              onClick={() => setClassification(type)}
              className={`p-3 border rounded text-xs transition-colors
                ${
                  classification === type
                    ? "bg-[#00FF9C]/20 border-[#00FF9C] text-white"
                    : "bg-black/40 border-white/10 text-slate-300 hover:border-[#00FF9C]"
                }`}
            >
              {type}
            </button>
          ))}

        </div>
      </div>

      <div className="space-y-2">
        <label className="text-xs text-[#00FF9C] font-bold uppercase">
          Evidence
        </label>
        <div className="border-2 border-dashed border-white/20 rounded-lg p-6 bg-black/20">
          <label className="cursor-pointer flex flex-col items-center justify-center border-2 border-dashed border-white/20 rounded-lg p-6 bg-black/20 hover:border-[#00FF9C]/50 transition text-center">
            <span className="text-slate-400 text-sm">
              Tap to upload evidence
            </span>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files[0];
                if (!file) return;

                const reader = new FileReader();
                reader.onloadend = () => {
                  setImage(reader.result);
                };
                reader.readAsDataURL(file);
              }}
              className="hidden"
            />
          </label>

          {image && (
            <div className="mt-4">
              <img
                src={image}
                alt="Preview"
                className="rounded-lg max-h-40 object-cover border border-white/10"
              />
            </div>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-xs text-[#00FF9C] font-bold uppercase">
          Location
        </label>
        <div className="bg-black/40 h-32 rounded border border-white/10 flex flex-col items-center justify-center text-xs relative overflow-hidden group p-4 text-center">
          <MapIcon size={18} className="mb-2 text-[#00FF9C]" />

          {location ? (
            <>
              <div className="text-white font-medium">
                Coordinates Detected
              </div>
              <div className="text-slate-400 mt-1">
                {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
              </div>
            </>
          ) : (
            <div className="text-slate-500">
              Detecting location...
            </div>
          )}
        </div>
      </div>

      <button
        disabled={!classification || !location || loadingReport}
        onClick={async () => {  
          if (!classification || !location) return;

          setLoadingReport(true);
          setReportError(null);
          setReportMessage(null);

          try {
            const data = await API.post("/api/report", {
              classification,
              latitude: location.latitude,
              longitude: location.longitude,
              image
            });
            setReportMessage(`Report submitted. Case ID: ${data.caseId}`);

            await API.post("/api/profile/update", {
              userId: "507f1f77bcf86cd799439011",
              actionType: "REPORT"
            });

          } catch (err) {
            setReportError("Failed to submit report");
          } finally {
            setLoadingReport(false);
          }
        }}
        className={`w-full py-3 md:py-4 border font-bold rounded-lg transition-all
          ${
            !classification || !location
              ? "bg-gray-700 text-gray-400 cursor-not-allowed"
              : "bg-gradient-to-r from-[#0B3D2E] to-[#125c44] border-[#00FF9C]/30 text-[#00FF9C] hover:shadow-[0_0_20px_rgba(0,255,156,0.2)]"
          }
        `}
      >
        {loadingReport ? "Submitting..." : "SUBMIT REPORT"}
      </button>

      {reportMessage && (
        <div className="text-sm mt-3 px-4 py-2 rounded bg-[#00FF9C]/10 border border-[#00FF9C]/30 text-[#00FF9C]">
          {reportMessage}
        </div>
      )}

      {reportError && (
        <div className="text-sm mt-3 px-4 py-2 rounded bg-red-500/10 border border-red-500/30 text-red-400">
          {reportError}
        </div>
      )}

    </GlassCard>
  </div>
  );
};


const InsightsScreen = () => {
  
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);
  const [analyticsError, setAnalyticsError] = useState(null);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const data = await API.get("/api/analytics/weekly");
        setAnalytics(data);
      } catch (err) {
        setAnalyticsError("Failed to load analytics");
      } finally {
        setLoadingAnalytics(false);
      }
    };

    fetchAnalytics();
  }, []);

  return (
  <div className="pt-24 px-4 max-w-5xl mx-auto space-y-6 animate-fadeIn pb-24">
    <h2 className="text-3xl font-bold text-white mb-6">Predictive Analytics</h2>

    <div className="grid md:grid-cols-3 gap-6">
      <GlassCard className="md:col-span-2 min-h-[300px] flex flex-col">
        <div className="flex justify-between mb-8">
          <h3 className="text-white font-bold flex items-center gap-2">
            <TrendingUp size={18} className="text-[#00FF9C]" /> 7-Day Trend
          </h3>
          <div className="flex gap-2 text-xs">
            <span className="text-[#00FF9C]">PM2.5</span>
            <span className="text-slate-500">PM10</span>
          </div>
        </div>
        <div className="flex-1 flex items-end gap-1 relative">
          <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-px bg-white/5 w-full"></div>
            ))}
          </div>
          {analytics?.weeklyTrend?.map((h, i) => (
            <div
              key={i}
              className="flex-1 bg-gradient-to-t from-emerald-900/0 to-[#00FF9C]/40 rounded-t hover:to-[#00FF9C]/80 transition-all duration-500 relative group"
              style={{ height: `${Math.min(h / 4, 100)}%` }}
            >
              <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-[10px] text-white opacity-0 group-hover:opacity-100 bg-black px-1 rounded">
                {h * 3}
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-between mt-4 text-xs text-slate-500 font-mono">
          <span>MON</span>
          <span>TUE</span>
          <span>WED</span>
          <span>THU</span>
          <span>FRI</span>
          <span>SAT</span>
          <span>SUN</span>
        </div>
      </GlassCard>

      <div className="space-y-6">
        <GlassCard hoverEffect>
          <div className="text-[#00FF9C] font-bold text-xs uppercase mb-2">
            Pattern Detected
          </div>
          {loadingAnalytics ? (
            <p className="text-slate-400">Analyzing trends...</p>
          ) : analyticsError ? (
            <p className="text-red-400">{analyticsError}</p>
          ) : (
            <p className="text-slate-200 text-sm leading-6">
              {analytics.insightSummary}
            </p>
          )}
        </GlassCard>

        <GlassCard>
          <div className="text-slate-400 text-xs uppercase mb-4">
            Ward Ranking
          </div>
          <div className="space-y-3">
            {analytics?.wardRanking?.map((ward, i) => (
              <div key={i} className="flex justify-between items-center text-sm">
                <div className="flex items-center gap-3">
                  <span className="text-slate-600 font-mono">
                    0{i + 1}
                  </span>
                  <span className="text-white">
                    {ward.ward}
                  </span>
                </div>
                <span className="text-[#00FF9C] font-bold">
                  {ward.urgencyScore}
                </span>
              </div>
            ))}
          </div>
        </GlassCard>
      </div>
    </div>
  </div>
  );
};




const ProfileScreen = () => {

  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileError, setProfileError] = useState(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [authMode, setAuthMode] = useState("login");

  const handleAuth = async () => {
    try {
      const endpoint =
        authMode === "login"
          ? "/api/auth/login"
          : "/api/auth/register";

      const payload =
        authMode === "login"
          ? { email, password }
          : { name, email, password };

      const data = await API.post(endpoint, payload);

      localStorage.setItem("gc_token", data.token);

      window.location.reload();
    } catch (err) {
      setProfileError("Authentication failed");
    }
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await API.get("/api/profile/me");
        setProfile(data);
      } catch (err) {
        setProfileError("Failed to load profile");
      } finally {
        setLoadingProfile(false);
      }
    };

    fetchProfile();
  }, []);

  if (!localStorage.getItem("gc_token")) {
    return (
      <div className="pt-24 px-4 max-w-md mx-auto space-y-6 animate-fadeIn pb-24">
        <GlassCard className="space-y-4">
          <h2 className="text-xl font-bold text-white">
            {authMode === "login" ? "Login" : "Register"}
          </h2>

          {authMode === "register" && (
            <input
              type="text"
              placeholder="Name"
              className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-white"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          )}

          <input
            type="email"
            placeholder="Email"
            className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-white"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <input
            type="password"
            placeholder="Password"
            className="w-full bg-black/40 border border-white/10 rounded px-3 py-2 text-white"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <button
            onClick={handleAuth}
            className="w-full bg-[#00FF9C] text-black py-2 rounded font-bold"
          >
            {authMode === "login" ? "Login" : "Register"}
          </button>

          <button
            onClick={() =>
              setAuthMode(authMode === "login" ? "register" : "login")
            }
            className="text-sm text-[#00FF9C]"
          >
            {authMode === "login"
              ? "Create account"
              : "Already have account?"}
          </button>
        </GlassCard>
      </div>
    );
  }

  return (
  <div className="pt-24 px-4 max-w-4xl mx-auto space-y-8 animate-fadeIn pb-24">
    <GlassCard className="flex items-center gap-6 p-8">
      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#0B3D2E] to-[#00FF9C] p-[2px]">
        <div className="w-full h-full rounded-full bg-black flex items-center justify-center">
          <User size={32} className="text-[#00FF9C]" />
        </div>
      </div>
      <div>
        {loadingProfile ? (
          <div className="text-slate-400">Loading profile...</div>
        ) : profileError ? (
          <div className="text-red-400">{profileError}</div>
        ) : (
          <>
            <h2 className="text-2xl font-bold text-white">
              {profile.name}
            </h2>
            <p className="text-slate-400">
              Rank: Top {profile.rankPercentile}%
            </p>
          </>
        )}
        <div className="flex gap-4 mt-3">
          <div className="text-xs bg-[#00FF9C]/10 text-[#00FF9C] px-2 py-1 rounded border border-[#00FF9C]/20">
            Top 1% Contributor
          </div>
          <div className="text-xs bg-white/5 text-slate-300 px-2 py-1 rounded border border-white/10">
            Ward 42
          </div>
        </div>
      </div>
    </GlassCard>

    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {profile && [
        { l: "Eco Points", v: profile.ecoPoints, i: Zap },
        { l: "Missions", v: profile.missionsCompleted, i: Target },
        { l: "Badges", v: profile.badges.length, i: Shield },
        { l: "Rank %", v: profile.rankPercentile, i: Leaf }
      ].map((stat, i) => (
        <GlassCard key={i} className="flex flex-col items-center justify-center py-6 gap-2">
          <stat.i size={20} className="text-slate-500 mb-1" />
          <div className="text-xl font-bold text-white">
            {stat.v}
          </div>
          <div className="text-xs text-slate-500 uppercase tracking-wider">
            {stat.l}
          </div>
        </GlassCard>
      ))}

      {profile && profile.badges.length > 0 && (
        <GlassCard>
          <h3 className="text-white font-bold mb-3">Achievements</h3>
          <div className="flex gap-2 flex-wrap">
            {profile.badges.map((badge, idx) => (
              <span
                key={idx}
                className="px-3 py-1 text-xs bg-[#00FF9C]/10 border border-[#00FF9C]/30 text-[#00FF9C] rounded-full"
              >
                {badge}
              </span>
            ))}
          </div>
        </GlassCard>
      )}

    </div>
  </div>
  );
};




export default function App() {
  const [currentScreen, setCurrentScreen] = useState("home");

  const [isAuthenticated, setIsAuthenticated] = useState(
    !!localStorage.getItem("gc_token")
  );

  useEffect(() => {
    const checkToken = () => {
      const token = localStorage.getItem("gc_token");

      if (!token) {
        setIsAuthenticated(false);
        return;
      }

      try {
        const payload = JSON.parse(atob(token.split(".")[1]));

        if (payload.exp * 1000 < Date.now()) {
          localStorage.removeItem("gc_token");
          setIsAuthenticated(false);
        } else {
          setIsAuthenticated(true);
        }

      } catch {
        localStorage.removeItem("gc_token");
        setIsAuthenticated(false);
      }
    };

    checkToken();
  }, []);

  const navItems = [
    { id: "home", icon: Wind, label: "Overview" },
    { id: "map", icon: MapIcon, label: "Map" },
    { id: "action", icon: Zap, label: "Action" },
    { id: "report", icon: AlertTriangle, label: "Report" },
    { id: "insights", icon: BarChart2, label: "Insights" },
    { id: "profile", icon: User, label: "Profile" },
  ];

  return (
    <div className="min-h-screen bg-[#020617] font-sans text-slate-200 selection:bg-[#00FF9C] selection:text-black overflow-x-hidden">
      <style>{`
        @keyframes drift {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        @keyframes softPulse {
          0%, 100% { box-shadow: 0 0 20px rgba(0,255,156,0.4); }
          50% { box-shadow: 0 0 35px rgba(0,255,156,0.7); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }
        @keyframes verticalSweep {
          0% { transform: translateX(0); }
          100% { transform: translateX(120vw); }
        }
        .animate-verticalSweep {
          animation: verticalSweep 8s linear infinite;
        }
        @keyframes breathe {
          0%,100% { 
            transform: scale(1);
            filter: drop-shadow(0 0 7px rgba(0,255,156,0.4));
          }
          50% { 
            transform: scale(1.05);
            filter: drop-shadow(0 0 12px rgba(0,255,156,0.7));
          }
        }
        .animate-breathe {
          animation: breathe 4s ease-in-out infinite;
        }
        .animate-drift { animation: drift 60s linear infinite; }
        .animate-pulse-slow { animation: pulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite; }
        .radial-gradient-green {
            background: radial-gradient(circle at center, rgba(0, 255, 156, 0.15) 0%, transparent 70%);
        }
        ::-webkit-scrollbar {
          width: 6px;
        }
        ::-webkit-scrollbar-thumb {
          background: rgba(0,255,156,0.4);
          border-radius: 4px;
        }
      `}</style>

      <AtmosphericBackground />

      <nav className="fixed top-4 left-1/2 -translate-x-1/2 z-50 
        w-[92%] h-20
        backdrop-blur-2xl 
      bg-[#051c15]/80 
        border border-[#00FF9C]/20 
        rounded-2xl
        shadow-[0_20px_60px_rgba(0,255,156,0.15)]
        flex items-center justify-between px-10">
        
        <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
          <div className="absolute top-0 left-[-20%] h-full w-[80px]
            bg-gradient-to-r
            from-transparent
            via-[#00FF9C]/25
            to-transparent
            blur-[18px]
            animate-verticalSweep">
          </div>
        </div>
        <div
          className="flex items-center gap-8 cursor-pointer"
          onClick={() => setCurrentScreen("home")}
        >
          <div className="relative w-12 h-12 flex items-center justify-center animate-breathe">
            <div className="absolute inset-0 bg-[#00FF9C]/20 blur-xl rounded-full group-hover:bg-[#00FF9C]/40 transition-all duration-500"></div>
            <img
              src="/favicon.svg"
              alt="VAYU VAEX Logo"
              className="relative w-10 h-10 object-contain drop-shadow-[0_0_12px_rgba(0,255,156,0.7)]"
            />
          </div>
          <span className="font-bold text-1xl md:text-2xl text-white tracking-wider">
            VAYU<span className="text-[#00FF9C]">VAEX </span>
          </span>
        </div>

        <div className="hidden md:flex items-center gap-10 text-sm font-medium">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setCurrentScreen(item.id)}
              className={`group relative px-5 py-2 rounded-xl text-sm font-medium 
              transition-all duration-300 flex items-center gap-2
              ${
                currentScreen === item.id
                  ? "bg-[#00FF9C]/10 text-[#00FF9C] border border-[#00FF9C]/30"
                  : "text-slate-400 hover:text-white border border-transparent hover:border-[#00FF9C]/20"
              }`}
            >
              <item.icon
                size={16}
                className={`transition-all duration-300 transform
                ${
                  currentScreen === item.id
                    ? "text-[#00FF9C] scale-105"
                    : "text-slate-500 group-hover:text-[#00FF9C] group-hover:scale-105"
                }`}
              />
              {item.label}

              {currentScreen === item.id && (
                <span className="absolute inset-0 rounded-xl bg-[#00FF9C]/5 blur-md -z-10"></span>
              )}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-4 text-[10px] uppercase tracking-[0.2em] text-[#00FF9C]">
          {isAuthenticated && (
            <button
              onClick={() => {
                localStorage.removeItem("gc_token");
                setIsAuthenticated(false);
                setCurrentScreen("home");
              }}
              className="text-xs border border-[#00FF9C]/30 px-3 py-1 rounded hover:bg-[#00FF9C]/10 transition"
            >
              Logout
            </button>
          )}

          <div className="flex items-center gap-2">
            <div className="relative">
              <div className="w-2 h-2 rounded-full bg-[#00FF9C]"></div>
              <div className="absolute inset-0 w-2 h-2 rounded-full bg-[#00FF9C] animate-ping opacity-60"></div>
            </div>
            System Online
          </div>
        </div>
      </nav>


      <main className="relative z-10 min-h-screen pt-24 transition-all duration-500">

        {currentScreen === "home" && <HomeScreen />}

        {currentScreen === "map" && <MapScreen />}

        {currentScreen === "action" && (
          isAuthenticated ? (
            <ActionScreen />
          ) : (
            <HomeScreen />
          )
        )}

        {currentScreen === "report" && <ReportScreen />}

        {currentScreen === "insights" && (
          isAuthenticated ? (
            <InsightsScreen />
          ) : (
            <HomeScreen />
          )
        )}

        {currentScreen === "profile" && <ProfileScreen />}

      </main>


      <div className="md:hidden fixed bottom-6 left-6 right-6 h-16 bg-black/80 backdrop-blur-xl border border-white/10 rounded-2xl flex items-center justify-between px-6 z-50 shadow-2xl">
        {navItems.slice(0, 5).map((item) => (
          <button
            key={item.id}
            onClick={() => setCurrentScreen(item.id)}
            className={`p-2 rounded-lg transition-all ${currentScreen === item.id ? "bg-[#00FF9C]/10 text-[#00FF9C]" : "text-slate-500"}`}
          >
            <item.icon size={20} />
          </button>
        ))}
      </div>
    </div>
  );
}